/* eslint-disable no-await-in-loop */
import { ActionButton } from '@bugbug/core/components/Button';
import { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { User } from '@bugbug/core/types/users';
import PublicPage from '~/components/PublicPage';
import { SCREEN_RESOLUTION_TYPE } from '~/constants/test';
import { useAsyncLegacyAction } from '~/modules/actionState/hooks/useAsyncLegacyAction';
import { ONBOARDING_STEP } from '~/modules/constans';
import { OrganizationActions } from '~/modules/organization/organization.redux';
import { selectDefaultProfile } from '~/modules/profile/profile.selectors';
import { useLazyGetProjectsInAllOrgsQuery } from '~/modules/projects/projects.api';
import { useAppDispatch, useAppSelector } from '~/modules/store';
import { useCreateTest } from '~/modules/tests/hooks/useCreateTest/useCreateTest';
import { UserActions } from '~/modules/user/user.redux';
import { selectUserData } from '~/modules/user/user.selectors';

import {
  LeftSection,
  MainContainer,
  PerkContainer,
  PerkText,
  PerksList,
  PlayIndicator,
  PlayButton,
  PlayIcon,
  PlayLabel,
  Title,
  TrialCheckIcon,
  TrialFreeIcon,
  TrialLockIcon,
  Video,
  VideoContainer,
  VideoGuide,
  VideoGuideLine,
  VideoThumbnail,
} from './TrialStart.styled';

export const TrialStart = () => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'onboarding.trialStart',
  });
  const { t: tRoot } = useTranslation();

  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUserData) as User;
  const defaultProfile = useAppSelector(selectDefaultProfile);
  const [getProjects, { isLoading: isLoadingProjects }] = useLazyGetProjectsInAllOrgsQuery();
  const [createTest, { isLoading: isCreatingTest }] = useCreateTest();
  const [startTrial, { isLoading: isStartingTrial }] = useAsyncLegacyAction(
    OrganizationActions.startTrialRequest,
  );

  const handleStartTrial = useCallback(async () => {
    await startTrial();

    const projects = await getProjects();
    const project = projects.data?.[0];
    if (!project) return;

    await createTest({
      name: t('firstTestName', 'My first test'),
      runProfileId: defaultProfile?.id,
      screenSizeType: SCREEN_RESOLUTION_TYPE.DESKTOP,
      projectId: project.id,
      projectSlug: project.slug,
    });
    dispatch(UserActions.skipOnboardingStepSuccess(ONBOARDING_STEP.TEST_CREATED));
  }, [createTest, defaultProfile?.id, dispatch, getProjects, startTrial, t]);

  const isLoading = isLoadingProjects || isCreatingTest || isStartingTrial;
  const [videoPlayerRef, setVideoPlayerRef] = useState<HTMLIFrameElement | null>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isVimeoLoaded, setIsVimeoLoaded] = useState(false);
  const [player, setPlayer] = useState<{ play: () => void } | null>(null);

  useEffect(() => {
    let isMounted = true;
    const script = document.createElement('script');
    script.src = 'https://player.vimeo.com/api/player.js';
    script.onload = () => {
      if (isMounted) {
        setIsVimeoLoaded(true);
      }
    };
    script.dataset.testid = 'VimeoScript';
    document.body.appendChild(script);

    return () => {
      isMounted = false;
      script.remove();
    };
  }, []);

  useEffect(() => {
    if (isVimeoLoaded && videoPlayerRef) {
      // @ts-expect-error missing global Vimeo types
      setPlayer(new Vimeo.Player(videoPlayerRef));
    }
  }, [isVimeoLoaded, videoPlayerRef]);

  const playVideo = () => {
    setIsVideoPlaying(true);
    player?.play();
  };

  return (
    <PublicPage>
      <MainContainer>
        <LeftSection>
          <Title>
            {user?.firstName ? (
              <Trans i18nKey="onboarding.trialStart.title">Hi {user?.firstName}!</Trans>
            ) : (
              <Trans i18nKey="onboarding.trialStart.titleGeneric">Hi there!</Trans>
            )}
          </Title>
          <PerksList>
            <PerkContainer>
              <TrialCheckIcon />
              <PerkText>
                <Trans i18nKey="onboarding.trialStart.perks.proPlan">
                  You've just started a{' '}
                  <strong>14-day free trial of advanced testing features.</strong>
                </Trans>
              </PerkText>
            </PerkContainer>
            <PerkContainer>
              <TrialLockIcon />
              <PerkText>
                {t(
                  'perks.premiumFeatures',
                  'Try all the premium features before you decide which plan is right for you.',
                )}
              </PerkText>
            </PerkContainer>
            <PerkContainer>
              <TrialFreeIcon />
              <PerkText>
                {t(
                  'perks.freePlan',
                  "When your trial ends, we'll automatically move you to the FREE plan.",
                )}
              </PerkText>
            </PerkContainer>
          </PerksList>
          <VideoGuide>
            {t('videoGuide', 'Quick start with our 2-minute video guide')}
            <VideoGuideLine />
          </VideoGuide>
          <ActionButton variant="primary" onClick={handleStartTrial} pending={isLoading}>
            {t('button', "Awesome, let's start")}
          </ActionButton>
        </LeftSection>
        <VideoContainer>
          <Video src={tRoot('default.docs.onboardingVideo')} ref={setVideoPlayerRef} />
          {!isVideoPlaying && (
            <PlayButton type="button" data-testid="PlayButton" onClick={playVideo}>
              <VideoThumbnail alt="" data-testid="VideoThumbnail" />
              <PlayIndicator ready={player !== null}>
                <PlayIcon />
                <PlayLabel>{t('playLabel', '2 min')}</PlayLabel>
              </PlayIndicator>
            </PlayButton>
          )}
        </VideoContainer>
      </MainContainer>
    </PublicPage>
  );
};
